import { useCallback, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { MbscCalendarEvent, MbscEventUpdateEvent } from "@mobiscroll/react";
import type { ProviderContext } from "notistack";
import type { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import type { UpdateSeriesBookingTimesResponse } from "../../../../features/Schedule/schedule.types";
import type { RefetchAllScheduleFn } from "../../../../features/Schedule/typings/refetch-schedule.types";
import {
  SeriesBookingEditMode,
  SeriesBookingScope
} from "../../../../features/Schedule/series-booking.types";
import { checkIndex, updateTimes } from "../../functions/schedule.functions";

export type SeriesCalendarPendingTimeEdit = {
  schedule: BookingScheduleInterface;
  bookingId: number;
  newStartTime: string;
  newEndTime: string;
  timezone: string;
  event: MbscEventUpdateEvent;
  selectedDateIso: string;
};

type UseSeriesCalendarTimeEditArgs = {
  calendarData: MbscCalendarEvent[];
  setCalendarData: Dispatch<SetStateAction<MbscCalendarEvent[]>>;
  enqueueSnackbar: ProviderContext["enqueueSnackbar"];
  refetchAllSchedule: RefetchAllScheduleFn;
};

/**
 * Outlook-style series scope + conflict flow for week-view drag/resize.
 * Keeps the optimistic calendar move until confirm/abort; restores oldEvent on cancel/error.
 */
export function useSeriesCalendarTimeEdit({
  calendarData,
  setCalendarData,
  enqueueSnackbar,
  refetchAllSchedule
}: UseSeriesCalendarTimeEditArgs) {
  const [pending, setPending] = useState<SeriesCalendarPendingTimeEdit | null>(null);
  const [seriesScopeDialogOpen, setSeriesScopeDialogOpen] = useState(false);
  const [pendingSeriesScope, setPendingSeriesScope] = useState<SeriesBookingScope | null>(null);
  const [seriesConflict, setSeriesConflict] = useState<UpdateSeriesBookingTimesResponse | null>(
    null
  );

  const restoreOptimisticDrag = useCallback(
    (pendingEdit: SeriesCalendarPendingTimeEdit | null = pending) => {
      if (!pendingEdit) {
        return;
      }
      checkIndex(
        calendarData,
        {
          bookingId: pendingEdit.bookingId,
          newStartTime: pendingEdit.newStartTime,
          newEndTime: pendingEdit.newEndTime,
          timezone: pendingEdit.timezone,
          event: pendingEdit.event
        },
        setCalendarData
      );
    },
    [calendarData, pending, setCalendarData]
  );

  const clearSeriesEditState = useCallback(() => {
    setPending(null);
    setPendingSeriesScope(null);
    setSeriesScopeDialogOpen(false);
    setSeriesConflict(null);
  }, []);

  const cancelSeriesTimeEdit = useCallback(() => {
    restoreOptimisticDrag();
    clearSeriesEditState();
  }, [clearSeriesEditState, restoreOptimisticDrag]);

  const beginSeriesTimeEdit = useCallback((next: SeriesCalendarPendingTimeEdit) => {
    setPending(next);
    setPendingSeriesScope(null);
    setSeriesConflict(null);
    setSeriesScopeDialogOpen(true);
  }, []);

  const runSeriesUpdate = useCallback(
    async (scope: SeriesBookingScope, mode: SeriesBookingEditMode) => {
      if (!pending) {
        return;
      }
      const pendingEdit = pending;
      await updateTimes(
        {
          bookingId: pendingEdit.bookingId,
          newStartTime: pendingEdit.newStartTime,
          newEndTime: pendingEdit.newEndTime,
          timezone: pendingEdit.timezone,
          event: pendingEdit.event
        },
        pendingEdit.schedule,
        calendarData,
        {
          enqueueSnackbar,
          setCalendarData,
          refetchAllSchedule,
          cancelChanges: () => {
            restoreOptimisticDrag(pendingEdit);
            clearSeriesEditState();
          }
        },
        {
          scope,
          mode,
          onConflict: conflict => {
            setSeriesConflict(conflict);
          }
        }
      ).then(outcome => {
        if (outcome === "success") {
          clearSeriesEditState();
        }
      });
    },
    [
      calendarData,
      clearSeriesEditState,
      enqueueSnackbar,
      pending,
      refetchAllSchedule,
      restoreOptimisticDrag,
      setCalendarData
    ]
  );

  const confirmScope = useCallback(
    (scope: SeriesBookingScope) => {
      setSeriesScopeDialogOpen(false);
      setPendingSeriesScope(scope);
      void runSeriesUpdate(scope, SeriesBookingEditMode.ALL_OR_NOTHING);
    },
    [runSeriesUpdate]
  );

  const handleConflictUpdateFreeDays = useCallback(() => {
    if (!pending || pendingSeriesScope == null) {
      cancelSeriesTimeEdit();
      return;
    }
    setSeriesConflict(null);
    void runSeriesUpdate(pendingSeriesScope, SeriesBookingEditMode.SKIP_CONFLICTS);
  }, [cancelSeriesTimeEdit, pending, pendingSeriesScope, runSeriesUpdate]);

  const handleConflictOnlySelectedDay = useCallback(() => {
    if (!pending) {
      return;
    }
    setSeriesConflict(null);
    void runSeriesUpdate(SeriesBookingScope.OCCURRENCE, SeriesBookingEditMode.ALL_OR_NOTHING);
  }, [pending, runSeriesUpdate]);

  const selectedDayIsFree =
    pending != null && seriesConflict?.freeBookingIds?.includes(pending.bookingId) === true;

  return {
    beginSeriesTimeEdit,
    cancelSeriesTimeEdit,
    confirmScope,
    handleConflictUpdateFreeDays,
    handleConflictOnlySelectedDay,
    seriesScopeDialogOpen,
    seriesConflict,
    selectedDayIsFree,
    selectedDateIso: pending?.selectedDateIso ?? "",
    timezone: pending?.timezone ?? "UTC",
    seriesBookingId: pending?.bookingId,
    seriesBookingType: pending?.schedule.bookingType ?? ""
  };
}
